import Link from "next/link";

import {
  ContactForm,
  type ContactFormCopy,
} from "@components/bluetooth-landing/ContactForm";
import { CopyAbleChip } from "@components/bluetooth-landing/CopyableChip";
import TextLogo from "@public/bluetooth-landing/current_color_text_logo.svg";

import { lightGradient } from "./ctaButtonStyles";

export interface FooterCopy {
  header: {
    h2: string;
    p: string;
  };
  cta: {
    href: string;
    text: string;
    p: string;
    email: string;
    phone: string;
  };
  links: {
    h4: string;
    links: {
      href: string;
      text: string;
    }[];
  };
  knowledgeBase: {
    h4: string;
    links: {
      href: string;
      text: string;
    }[];
  };
  copyright: string;
  privacyPolicy: string;
  privacyPolicyHref?: string;
  contactForm: ContactFormCopy;
  copiedLabel?: string;
}

export function Footer({ copy }: { copy: FooterCopy }) {
  const {
    header,
    cta,
    links,
    knowledgeBase,
    copyright,
    privacyPolicy,
    privacyPolicyHref,
    contactForm,
    copiedLabel,
  } = copy;

  return (
    <footer className="bg-surface-dark text-gray-300">
      <div id="kontakt" className="text-white" style={{ backgroundImage: lightGradient }}>
        <div className="container mx-auto px-6 md:px-12 py-20 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-center pr-[48px]">
            <div className="md:w-1/2 bg-surface-dark/80 p-8 rounded-2xl mr-[-40px] mt-[12rem]">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                {header.h2}
              </h2>
              <p className="text-blue-100 text-lg max-w-lg">{header.p}</p>
            </div>
            <div className="md:w-1/2 w-full max-w-md">
              <ContactForm copy={contactForm} />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 py-12 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 border-b border-gray-800 pb-12 mb-8">
          <div className="col-span-1 md:col-span-2">
            <Link
              href="/"
              className="text-white inline-flex items-center mb-6"
              aria-label="Blues Brackets"
            >
              <TextLogo className="w-32 md:w-40 h-auto text-white" />
            </Link>
            <p className="text-gray-400 max-w-sm mb-6">{cta.p}</p>
            <div className="flex flex-col gap-4 items-start">
              <CopyAbleChip
                href={cta.email}
                text={cta.email}
                icon="mail"
                copiedLabel={copiedLabel}
              />
              <CopyAbleChip
                href={cta.phone}
                text={cta.phone}
                icon="phone"
                copiedLabel={copiedLabel}
              />
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{links.h4}</h4>
            <ul className="space-y-2">
              {links.links.map(link => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 md:col-span-2">
            <h4 className="text-white font-semibold mb-4">{knowledgeBase.h4}</h4>
            <ul className="space-y-2">
              {knowledgeBase.links.map(link => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>
            &copy; {new Date().getFullYear()} {copyright}
          </p>
          <div className="flex gap-6">
            <a
              href={privacyPolicyHref ?? privacyPolicy}
              className="hover:text-white transition-colors"
            >
              {privacyPolicy}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
